import { Modal } from "@circle-react-uikit/ModalV2";
import { Typography } from "@circle-react-uikit/Typography";
import { useModal } from "@circle-react/components/Layout/ModalManagerProvider";

import { t } from "@/i18n-js/instance";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";

import { Loader } from "../../Loader";
import { UpgradePlanModalBody } from "../../UpgradePlanModalBody";
import { BlockingFeatures } from "../BlockingFeatures";
import { useManagePlanTrialModalLogic } from "./useManagePlanTrialModalLogic";

export const ManagePlanTrialModalUIKitComponent = () => {
  const modal = useModal();
  const {
    isCancelled,
    isLoading,
    isCheckingDowngrade,
    hasBlockers,
    canDowngradeData,
    currentPlan,
    planTrialEndDateFormatted,
    planTrialDaysRemainingInWords,
    trackModalEvent,
    destroyPlanTrial,
  } = useManagePlanTrialModalLogic({ isVisible: modal.visible });

  const handleKeepTrial = async () => {
    trackModalEvent("manage_plan_trial_modal.keep_trial.clicked");
    await modal.hide();
    modal.remove();
  };

  const handleCancelTrial = async () => {
    trackModalEvent("manage_plan_trial_modal.cancel_trial.clicked", {
      cannot_downgrade: canDowngradeData && !canDowngradeData.can_downgrade,
    });
    await destroyPlanTrial();
  };

  const handleBackToCommunity = async () => {
    trackModalEvent("manage_plan_trial_modal.back_to_community.clicked");
    await modal.hide();
    modal.remove();
  };

  const handleClose = async () => {
    trackModalEvent("manage_plan_trial_modal.closed", {
      was_cancelled: isCancelled,
    });
    await modal.hide();
    modal.remove();
  };

  if (isCheckingDowngrade || (isLoading && !isCancelled)) {
    return (
      <Modal
        title={t("manage_plan_trial_modal.title")}
        isOpen={modal.visible}
        onClose={handleClose}
      >
        <Modal.Overlay />
        <Modal.Content size="md">
          <Loader
            title={
              isLoading
                ? t("manage_plan_trial_modal.canceling_trial")
                : t("pricing_plans.upgrade_modal.loading.validating_limits")
            }
          />
        </Modal.Content>
      </Modal>
    );
  }

  if (isCancelled) {
    return (
      <Modal
        title={t("manage_plan_trial_modal.cancelled_title")}
        isOpen={modal.visible}
        onClose={handleClose}
      >
        <Modal.Overlay />
        <Modal.Content size="md">
          <Modal.Header>
            <Modal.Title>
              {t("manage_plan_trial_modal.cancelled_title")}
            </Modal.Title>
            <Modal.HeaderActions>
              <Modal.CloseButton onClick={handleClose} />
            </Modal.HeaderActions>
          </Modal.Header>
          <UpgradePlanModalBody>
            <div className="py-6 text-center">
              <Typography.BodyLg>
                {t("manage_plan_trial_modal.cancelled_message", {
                  planName: currentPlan?.display_name,
                })}
              </Typography.BodyLg>
            </div>
          </UpgradePlanModalBody>
          <Modal.Footer className="mt-5 flex justify-center">
            <Button variant="primary" large onClick={handleBackToCommunity}>
              {t("manage_plan_trial_modal.back_to_community_button")}
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    );
  }

  return (
    <Modal
      title={t("manage_plan_trial_modal.title")}
      isOpen={modal.visible}
      onClose={handleClose}
    >
      <Modal.Overlay />
      <Modal.Content size="md">
        <Modal.Header>
          <Modal.Title>{t("manage_plan_trial_modal.title")}</Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton onClick={handleClose} />
          </Modal.HeaderActions>
        </Modal.Header>
        <UpgradePlanModalBody>
          <div className="mb-6 space-y-4">
            <Typography.BodyMd>
              {t("manage_plan_trial_modal.trial_description", {
                daysRemaining: planTrialDaysRemainingInWords,
                trialEndDate: planTrialEndDateFormatted,
              })}
            </Typography.BodyMd>

            {hasBlockers &&
              canDowngradeData?.blocking_features &&
              canDowngradeData.blocking_features.length > 0 && (
                <BlockingFeatures
                  blockingFeatures={canDowngradeData.blocking_features}
                  i18nRoot="pricing_plans.upgrade_modal.interval_switch"
                />
              )}
          </div>
        </UpgradePlanModalBody>
        <Modal.Footer className="mt-5 flex justify-end gap-x-3">
          <Button
            variant="secondary"
            large
            onClick={handleKeepTrial}
            disabled={isLoading}
          >
            {t("manage_plan_trial_modal.keep_trial_button")}
          </Button>
          <Button
            variant="primary"
            large
            onClick={handleCancelTrial}
            disabled={isLoading || hasBlockers}
          >
            {isLoading
              ? t("manage_plan_trial_modal.canceling")
              : t("manage_plan_trial_modal.cancel_trial_button")}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
